import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation } from 'swiper/modules';
import 'swiper/css';
import Modal from '@/app/Components/Modal.jsx';
import YoutubeEmbed from '@/app/Components/YoutubeEmbed.jsx';
import PlayIcon from '@assets/play.png';

const Slide = styled(SwiperSlide)`
    height: 576px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.75);
    user-select: none;
    img {
        height: 100%;
        object-fit: contain;
        object-position: center;
    }

    @media (max-width: 1000px) {
        height: auto;
        max-height: 60vh;
    }
    @media (max-width: 640px) {
        max-height: 42vh;
    }
    body:not(.dark) & {
        background: rgba(0, 0, 0, 0.98);
    }
`;

const ThumbsSwiper = styled(Swiper)`
    &:after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 120px;
        background: linear-gradient(90deg, rgba(24,26,30,0) 0%, rgba(24,26,30,1) 100%);
        z-index: 1;
    }
    &.is-end:after {
        z-index: -1;
        opacity: 0;
    }
    @media (max-width: 1000px) {
        &:after {
            z-index: -1;
        }
    }
    body:not(.dark) &:before, body:not(.dark) &:after {
        display: none !important;
    }
`;

const ThumbSlide = styled(SwiperSlide)`
    height: 10rem;
    width: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 6px;
    transition: opacity 0.2s ease;
    opacity: 0.65;
    border: 1px solid transparent;
    user-select: none;
    &.swiper-slide-thumb-active {
        opacity: 1;
        border-color: rgba(200, 200, 200, 0.4);
    }
    .video-thumbnail {
        border-radius: 6px;
        height: 100%;
        position: relative;
        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 6px;
            background: rgba(0, 0, 0, 0.5);
        }
    }
    img:first-child {
        border-radius: 6px;
        object-position: center;
        object-fit: cover;
        height: 100%;
    }
    @media (max-width: 1000px) {
        height: 6rem;
    }
`;

const NavigationElement = styled.button`
    cursor: pointer;
    border-radius: 6rem;
    outline: none;
    background: rgba(10, 10, 10, 0.75);
    box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.42);
    height: 42px;
    width: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }

    @media screen and (max-width: 640px) {
        top: 40% !important;
    }
`;

function youtubeId(url) {
    if (!url) return null;

    try {
        return new URL(url).searchParams.get('v');
    } catch {
        return null;
    }
}

export default function GameDetailsModal({ game, locale, onClose }) {
    const swiperRef = useRef(null);
    const thumbsSwiperRef = useRef(null);
    const navigationNextRef = useRef(null);
    const navigationPrevRef = useRef(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const trailerId = youtubeId(game?.trailer_url);
    const screenshots = game?.screenshots ?? [];

    const updateThumbsEndState = useCallback((isEnd) => {
        const thumbsEl = thumbsSwiperRef.current?.el;
        if (!thumbsEl) return;
        thumbsEl.classList.toggle('is-end', isEnd);
    }, []);

    const updateSwiperNavigation = useCallback(() => {
        const swiper = swiperRef.current;
        if (!swiper) return;
        if (navigationPrevRef.current) navigationPrevRef.current.disabled = swiper.isBeginning;
        if (navigationNextRef.current) navigationNextRef.current.disabled = swiper.isEnd;
        updateThumbsEndState(swiper.isEnd);
    }, [updateThumbsEndState]);

    const goToSlide = useCallback((index) => {
        swiperRef.current?.slideTo(index);
        setActiveSlide(index);
    }, []);

    useEffect(() => {
        setActiveSlide(0);
        swiperRef.current = null;
        thumbsSwiperRef.current = null;
    }, [game?.id]);

    const description = typeof game?.description === 'string'
        ? game.description
        : game?.description?.[locale] ?? game?.description?.fr ?? '';

    return <Modal show={Boolean(game)} onClose={onClose} maxWidth="5xl" withCloseButton insetCloseButton>
        {game && <>
            <Swiper
                key={`game-modal-${game.id}`}
                modules={[A11y]}
                spaceBetween={0}
                slidesPerView={1}
                onSwiper={(swiper) => { swiperRef.current = swiper; }}
                onSlideChange={(swiper) => {
                    setActiveSlide(swiper.activeIndex);
                    if (!navigationPrevRef.current || !navigationNextRef.current) return;
                    navigationPrevRef.current.disabled = swiper.isBeginning;
                    navigationNextRef.current.disabled = swiper.isEnd;
                    updateThumbsEndState(swiper.isEnd);
                }}
                onAfterInit={(swiper) => {
                    if (navigationPrevRef.current) navigationPrevRef.current.disabled = true;
                    if (navigationNextRef.current && swiper.isEnd) navigationNextRef.current.disabled = true;
                    updateThumbsEndState(swiper.isEnd);
                }}
                onResize={updateSwiperNavigation}
                onUpdate={updateSwiperNavigation}
            >
                {trailerId && <SwiperSlide><YoutubeEmbed embedId={trailerId} /></SwiperSlide>}
                {screenshots.map((s, i) => <Slide key={`slide_${i}`}><img src={s} alt="" loading="lazy" /></Slide>)}
            </Swiper>
            <div className="relative px-2 md:px-10 pt-6 md:pt-10">
                <ThumbsSwiper
                    key={`game-modal-thumbs-${game.id}`}
                    modules={[Navigation, A11y]}
                    navigation
                    spaceBetween={16}
                    slidesPerView="auto"
                    onSwiper={(swiper) => { thumbsSwiperRef.current = swiper; }}
                >
                    {trailerId && <ThumbSlide className={activeSlide === 0 ? 'swiper-slide-thumb-active' : ''} onClick={() => goToSlide(0)}>
                        <div className="video-thumbnail">
                            <img src={`https://img.youtube.com/vi/${trailerId}/hqdefault.jpg`} alt="" loading="lazy" />
                            <img src={PlayIcon} alt="Play" className="size-10 absolute z-10 top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2" />
                        </div>
                    </ThumbSlide>}
                    {screenshots.map((s, i) => {
                        const slideIndex = (trailerId ? 1 : 0) + i;
                        return <ThumbSlide key={`thumb_${i}`} className={activeSlide === slideIndex ? 'swiper-slide-thumb-active' : ''} onClick={() => goToSlide(slideIndex)}>
                            <img src={s} alt="" loading="lazy" />
                        </ThumbSlide>;
                    })}
                </ThumbsSwiper>
                <NavigationElement className="absolute left-2 md:left-16 top-[50%] z-50" ref={navigationPrevRef} onClick={() => swiperRef.current?.slidePrev()} aria-label="Previous media">
                    <svg className="size-5 rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </NavigationElement>
                <NavigationElement className="absolute right-2 md:right-16 top-[50%] z-50" ref={navigationNextRef} onClick={() => swiperRef.current?.slideNext()} aria-label="Next media">
                    <svg className="size-5 -rotate-90" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </NavigationElement>
            </div>
            <div className="flex flex-col px-5 md:px-10 pt-4 md:pt-8 pb-10">
                <h3 className="font-semibold dark:text-white text-gray-900 text-lg md:text-xl mb-1 md:mb-4">{game.name}</h3>
                <p className="dark:text-light-gray text-gray-700 text-base md:text-md">{description}</p>
            </div>
        </>}
    </Modal>;
}
